import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import { freeze } from 'immer';
import _omit from 'lodash/omit';
import ContextBuilder from '../../../../hooks/computeContext/context/ContextBuilder';
import ContextFactory from '../../../../hooks/computeContext/context/ContextFactory';
// oxlint-disable-next-line no-cycle -- computeContext ↔ store utils; navigation types live in navigationForContextTypes
import { getInterfaceRecordForComputeContext } from '../../../../hooks/computeContext/utils/getInterfaceRecordForComputeContext';
import { isDynamicValue } from '../../../../utils/dynamicBindings';
import { getNoCodeComputedData } from '../../../../utils/getNoCodeComputeData';
import { getShouldEvaluateResult } from '../../../../utils/getShouldEvaluateResult';
import { sanitizeVariableValue } from '../../../../utils/sanitizeVariableValue';
import { smartConvertStringToObject } from '../../../../utils/smartConvertStringToObject';
import type { GlobalStateStore } from '../../../GlobalStateStore';
import type { GlobalAppStateStore } from '../types';

/**
 * Evaluates a single page variable's initial value
 * Returns the evaluated state with value and isInitialised flag
 */
export function evaluatePageVariableInitialValue(
  pageVariable: JavaScriptVariable,
  context?: Record<string, unknown>,
  shouldEvaluateResult: (result: string) => boolean = isDynamicValue,
): { value: unknown; error?: string } {
  if (pageVariable.initialValue === undefined) {
    return { value: undefined };
  }

  // note: this isDynamicValue asks "is there an expression here at all", which is a
  // different question from shouldEvaluateResult's "re-evaluate the result of one".
  if (isDynamicValue(pageVariable.initialValue)) {
    if (context !== undefined) {
      let errorMessage: string | undefined;
      const { value: computedValue } = getNoCodeComputedData<{ value: unknown }>(
        { value: pageVariable.initialValue },
        context,
        shouldEvaluateResult,
        undefined,
        (e: unknown) => {
          errorMessage = e instanceof Error ? e.message : String(e);
        },
      );
      return {
        value: freeze(sanitizeVariableValue(computedValue, pageVariable.type), true),
        error: errorMessage,
      };
    }
    // context not available, skip initialization
    return { value: undefined };
  }

  // convert non-dynamic values
  const convertedValue = smartConvertStringToObject(pageVariable.initialValue, pageVariable.type);
  return {
    value: freeze(sanitizeVariableValue(convertedValue, pageVariable.type), true),
  };
}

function createContextForPageVariables({
  appState,
  blocks,
  pageInputs,
  pageParams,
}: {
  appState: GlobalAppStateStore;
  blocks: Record<string, BlockStateUnionType | undefined>;
  pageInputs: Record<string, unknown>;
  pageParams: Record<string, unknown>;
}): Record<string, unknown> {
  const minimalContextBuilder = new ContextBuilder();
  const interfaceRecordForContext = getInterfaceRecordForComputeContext({
    appGlobalState: appState,
  });

  // use page context builder if blocks and pageInputs are available (page-level store)
  if (blocks !== undefined && pageInputs !== undefined) {
    return ContextFactory.createPageContextBuilder(
      {
        state: {
          blocks: blocks ?? {},
          blockRefs: {},
          pageInputs: pageInputs ?? {},
          pageParams: pageParams ?? {},
          pageVariables: {},
          pageFunctions: {},
          dataSources: {},
          dataSourcesMeta: {},
        },
        appState: appState,
        interfaceRecord: interfaceRecordForContext,
        blocksRuntimeState: undefined,
        blockMethods: {},
        dataSourceMethods: {},
        localStorageMethods: {},
        sessionStorageMethods: {},
        pageVariableMethods: {},
        pageFunctionMethods: {},
        mediaUploads: {},
        queryMeta: {},
        actions: {},
        walkthrough: {},
      },
      minimalContextBuilder,
    ).build();
  }

  return ContextFactory.createAppContextBuilder(
    {
      appState: appState,
      interfaceRecord: interfaceRecordForContext,
      dataSourceMethods: {},
      localStorageMethods: {},
      sessionStorageMethods: {},
      pageVariableMethods: {},
      pageFunctionMethods: {},
      actions: {},
      queryMeta: {},
      walkthrough: {},
    },
    minimalContextBuilder,
  ).build();
}

/**
 * the data that is available when app store is being created is the only data availabe to evaluated app variables
 * for page variables, entire appState + page blocks state and page inputs are available
 */
export function getInitialPageVariablesState({
  pageVariables,
  blocks,
  pageInputs,
  pageParams,
  pageVariablesInitialValue = EMPTY_OBJECT,
  appState,
}: {
  pageVariables: NonNullable<InterfacePageEntity['properties']['pageVariables']>;
  blocks: Record<string, BlockStateUnionType | undefined>;
  pageInputs: Record<string, unknown>;
  pageParams: Record<string, unknown>;
  // ID-keyed initial values for page variables (from delta-matrix appVariables or other sources)
  pageVariablesInitialValue?: Record<string, unknown>;
  appState: GlobalAppStateStore;
}) {
  const result: GlobalStateStore['pageVariables'] = {};
  const pageVariableKeys = Object.keys(pageVariables);
  const pageVariableKeysLength = pageVariableKeys.length;

  // this runs while BaseGlobalAppStateStoreProvider is still building its own store, so
  // the flag has to come off the record on appState — the store hook does not exist yet.
  const shouldEvaluateResult = getShouldEvaluateResult(
    appState?.interfaceRecord?.properties.flags,
  );

  let contextForPageVariables: Record<string, unknown> | undefined;
  if (appState) {
    contextForPageVariables = createContextForPageVariables({
      appState,
      blocks,
      pageInputs,
      pageParams,
    });
  }

  for (let i = 0; i < pageVariableKeysLength; i++) {
    const key = pageVariableKeys[i];
    const pageVariable = pageVariables[key];
    const omitted = _omit(pageVariable, ['initialValue']);

    // check if pageVariablesInitialValue provides a value for this variable (highest priority)
    if (key in pageVariablesInitialValue) {
      result[key] = {
        ...omitted,
        value: freeze(
          sanitizeVariableValue(pageVariablesInitialValue[key], pageVariable.type),
          true,
        ),
      };
      continue;
    }

    // else, evaluate initialValue as before
    const { value: finalValue } = evaluatePageVariableInitialValue(
      pageVariable,
      contextForPageVariables,
      shouldEvaluateResult,
    );

    result[key] = {
      ...omitted,
      value: finalValue,
    };
  }

  return result;
}
